import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Typography
} from "@mui/material";
import { COLORS, disabledContainedButtonSx } from "assets";
import { MyModal } from "components";
import { ENDPOINTS } from "config/constants";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { useSnackbar } from "notistack";
import { MouseEventHandler, useCallback, useEffect, useState } from "react";
import { BoxModel } from "types";

interface BulkRemoveBoxesProps {
  boxes: BoxModel[];
  onSuccess: () => void;
  onClose: () => void;
}

export default function BulkRemoveBoxes({ boxes, onSuccess, onClose }: BulkRemoveBoxesProps) {
  const [checked, setChecked] = useState(false);
  const t = useTranslation();
  const { isLoading, response, error, callFetch } = useFetch<{
    updated: string[];
    failed: { id: string }[];
  }>("DELETE");
  const { enqueueSnackbar } = useSnackbar();

  const onSubmit: MouseEventHandler<HTMLButtonElement> = useCallback(
    (e) => {
      e.preventDefault();
      callFetch(ENDPOINTS.boxes + "/bulk", {
        body: JSON.stringify({ ids: boxes.map((b) => b._id) })
      }).catch(console.error);
    },
    [boxes, callFetch]
  );

  useEffect(() => {
    if (response) {
      const failed = response.data?.failed || [];
      enqueueSnackbar(response.message || "", { variant: failed.length ? "warning" : "success" });
      if (failed.length) {
        const names = failed
          .map((f) => {
            const box = boxes.find((b) => b._id === f.id);
            return box?.name || box?.deviceId || f.id;
          })
          .join(", ");
        enqueueSnackbar(`Failed: ${names}`, { variant: "error" });
      }
      onSuccess();
      onClose();
    }
  }, [response, enqueueSnackbar, onSuccess, onClose, boxes]);

  useEffect(() => {
    if (error) enqueueSnackbar(error.message || "", { variant: "error" });
  }, [error, enqueueSnackbar]);

  return (
    <MyModal
      title={t["Remove Box"] || "Remove Boxes"}
      subtitle={t["Are You Sure You Want to Remove?"]}
      open={true}
      setOpen={onClose}
      variant="light"
    >
      <Typography sx={{ color: COLORS.surface.secondaryText, fontSize: 14, mb: 2 }}>
        {boxes.length} {boxes.length === 1 ? "box" : "boxes"}:{" "}
        {boxes.map((b) => b.name || b.deviceId).join(", ")}
      </Typography>
      <FormControl fullWidth sx={{ mb: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={(_, value) => setChecked(value)}
              sx={{
                color: COLORS.surface.secondaryText,
                "&.Mui-checked": { color: COLORS.error }
              }}
            />
          }
          label={
            <Typography sx={{ fontSize: 14, color: COLORS.surface.secondaryText }}>
              {t["I confirm that removing this is an irreversible action and i want to proceed."]}
            </Typography>
          }
        />
      </FormControl>
      <Box display="flex" justifyContent="flex-end" gap={1.5} mt={3}>
        <Button
          type="button"
          variant="outlined"
          onClick={onClose}
          disabled={isLoading}
          sx={{
            minWidth: 120,
            height: 44,
            borderRadius: `${COLORS.radius.md}px`,
            borderColor: COLORS.surface.border,
            color: COLORS.surface.primaryText,
            fontWeight: 500,
            textTransform: "none"
          }}
        >
          {t["Cancel"]}
        </Button>
        <Button
          type="button"
          variant="contained"
          disabled={!checked || isLoading}
          endIcon={isLoading ? <CircularProgress size={18} /> : undefined}
          onClick={onSubmit}
          sx={{
            minWidth: 140,
            height: 44,
            borderRadius: `${COLORS.radius.md}px`,
            fontWeight: 500,
            textTransform: "none",
            bgcolor: COLORS.error,
            color: COLORS.white,
            boxShadow: "none",
            "&:hover": { bgcolor: COLORS.error, filter: "brightness(1.05)", boxShadow: "none" },
            "&.Mui-disabled": disabledContainedButtonSx
          }}
        >
          {t["Remove"]}
        </Button>
      </Box>
    </MyModal>
  );
}
